import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Truck, Calendar, MapPin, Package, User, Phone,
  Clock, RefreshCw, Loader2, Route, CheckCircle, Circle,
  Weight, Sun, Moon, FileText, AlertCircle, Scale
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useClientPermissions } from '../hooks/useClientPermissions';
import TransportMap from '../components/TransportMap';
import TransferModificationRequestsSection from '../components/TransferModificationRequestsSection';
import PodValidationBanner from '../components/PodValidationBanner';
import TransferStopsTimeline from '../components/transfers/TransferStopsTimeline';
import { cn } from '../lib/utils';
import { getStatusLabel } from '../constants/statuses';
import { formatVehicleType, formatOperationType, formatMerchandiseType } from '../utils/formatters';

const token = 'cookie-auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  planifiee: { label: 'Planifié', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: Clock },
  planned: { label: 'Planifié', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: Clock },
  en_cours_de_chargement: { label: 'En chargement', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: Package },
  en_transit: { label: 'En transit', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Route },
  en_cours_de_dechargement: { label: 'En déchargement', color: 'bg-purple-100 text-purple-700 border-purple-300', icon: MapPin },
  completed: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700 border-red-300', icon: AlertCircle },
};

const PROGRESS_STEPS = [
  { key: 'planned', label: 'Planifié', icon: Calendar },
  { key: 'chargement', label: 'Chargement', icon: Package },
  { key: 'transit', label: 'En transit', icon: Truck },
  { key: 'dechargement', label: 'Déchargement', icon: MapPin },
  { key: 'completed', label: 'Terminé', icon: CheckCircle },
];

const getProgressStage = (status) => {
  if (['cancelled'].includes(status)) return -1;
  if (['planned', 'planifiee'].includes(status)) return 1;
  if (['en_cours_de_chargement'].includes(status)) return 2;
  if (['en_transit'].includes(status)) return 3;
  if (['en_cours_de_dechargement'].includes(status)) return 4;
  if (['completed'].includes(status)) return 5;
  return 1;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
  } catch { return dateStr; }
};

const formatTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleTimeString('fr-FR', {
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return '-'; }
};

const getDerivedStopStatus = (transferStatus, stopIndex, totalStops) => {
  const stage = getProgressStage(transferStatus);
  const isFirst = stopIndex === 0;
  const isLast = stopIndex === totalStops - 1;
  if (stage >= 5) return { label: 'Termine', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
  if (stage === -1) return { label: 'Annule', cls: 'bg-red-100 text-red-700 border-red-200' };
  if (isFirst) {
    if (stage >= 3) return { label: 'Charge', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
    if (stage === 2) return { label: 'Chargement', cls: 'bg-blue-100 text-blue-700 border-blue-200' };
    return { label: 'En attente', cls: 'bg-gray-100 text-gray-600 border-gray-200' };
  }
  if (isLast) {
    if (stage >= 5) return { label: 'Livre', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
    if (stage === 4) return { label: 'Dechargement', cls: 'bg-purple-100 text-purple-700 border-purple-200' };
    if (stage === 3) return { label: 'En route', cls: 'bg-amber-100 text-amber-700 border-amber-200' };
    return { label: 'En attente', cls: 'bg-gray-100 text-gray-600 border-gray-200' };
  }
  return { label: 'En cours', cls: 'bg-blue-100 text-blue-700 border-blue-200' };
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.planned;
  const Icon = config.icon;
  return (
    <Badge className={cn('flex items-center gap-1.5 px-3 py-1 text-sm border', config.color)} data-testid="transfer-status">
      <Icon className="w-4 h-4" />
      {config.label}
    </Badge>
  );
};

const ClientTransferDetail = () => {
  const { contractId, runId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = useClientPermissions();
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasSignedUnloading, setHasSignedUnloading] = useState(false);

  const fetchTransfer = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contract-runs/${runId}`, {
        headers: { 'Authorization': 'Bearer cookie-auth' }
      });
      if (!response.ok) throw new Error('Erreur de chargement');
      setTransfer(await response.json());

      // Check si un bon de déchargement signé existe
      try {
        const notesRes = await fetch(`${API_URL}/api/operations/${runId}/loading-notes`, {
          headers: { 'Authorization': 'Bearer cookie-auth' }
        });
        if (notesRes.ok) {
          const notes = await notesRes.json();
          const items = Array.isArray(notes) ? notes : (notes.items || []);
          const signed = items.some(n => n.type === 'unloading'
            && (n.status === 'signed' || (Array.isArray(n.signatures) && n.signatures.length >= 2)));
          setHasSignedUnloading(signed);
        }
      } catch { /* noop */ }
    } catch (error) {
      console.error('Error fetching transfer:', error);
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { fetchTransfer(); }, [fetchTransfer]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!transfer) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
        <h2 className="text-xl font-semibold text-gray-700">Transfert non trouvé</h2>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
      </div>
    );
  }

  const progressStage = getProgressStage(transfer.status);
  const stops = transfer.stops || [];
  const departureAddress = stops[0]?.address || 'Non défini';
  const arrivalAddress = stops[stops.length - 1]?.address || 'Non défini';
  const departureCity = departureAddress.split(',')[0];
  const arrivalCity = arrivalAddress.split(',')[0];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-transfer-detail">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} className="p-2" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-gray-900" data-testid="transfer-ref">
                  {transfer.runNumber || `TRF-${transfer.id?.slice(0, 8)}`}
                </h1>
                <StatusBadge status={transfer.status} />
              </div>
              <p className="text-sm text-gray-500">
                <Calendar className="w-3.5 h-3.5 inline mr-1" />
                {formatDate(transfer.date)}
                {' '}
                {transfer.shift === 'day'
                  ? <><Sun className="w-3.5 h-3.5 inline mx-1" /> Jour</>
                  : <><Moon className="w-3.5 h-3.5 inline mx-1" /> Nuit</>
                }
                {transfer.contractNumber && ` | Contrat ${transfer.contractNumber}`}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchTransfer} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Progress Stepper */}
      {transfer.status !== 'cancelled' && (
        <div className="bg-white border-b px-6 py-3" data-testid="progress-stepper">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            {PROGRESS_STEPS.map((step, idx) => {
              const stepIndex = idx + 1;
              const isActive = stepIndex === progressStage;
              const isPast = stepIndex < progressStage;
              const Icon = step.icon;
              return (
                <React.Fragment key={step.key}>
                  <div className="flex flex-col items-center gap-1.5">
                    <div className={cn(
                      "w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all",
                      isPast ? "bg-emerald-500 border-emerald-500 text-white" :
                      isActive ? "bg-emerald-500 border-emerald-500 text-white" :
                      "bg-white border-gray-300 text-gray-400"
                    )}>
                      {isPast ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span className={cn(
                      "text-xs font-medium",
                      isPast || isActive ? "text-emerald-700" : "text-gray-400"
                    )}>
                      {step.label}
                    </span>
                  </div>
                  {idx < PROGRESS_STEPS.length - 1 && (
                    <div className="flex-1 h-0.5 mx-3">
                      <div className={cn(
                        "h-full rounded-full transition-all",
                        stepIndex < progressStage ? "bg-emerald-500" : "bg-gray-200"
                      )} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* Main Content - Two Column Layout */}
      <div className="p-6">
        {/* Bandeau POD — Phase 3b */}
        <div className="mb-4">
          <PodValidationBanner
            targetKind="transfer"
            contractId={contractId}
            runId={runId}
            label={transfer.runNumber || transfer.operationNumber || `Transfert #${runId.slice(0, 8)}`}
            clientValidation={transfer.clientValidation}
            hasSignedUnloadingNote={hasSignedUnloading}
            onChange={fetchTransfer}
          />
        </div>

        <div className="flex gap-6">
          {/* Left Column - 65% */}
          <div className="flex-1 space-y-6" style={{ maxWidth: '65%' }}>

            {/* Map + Trajet */}
            <Card className="overflow-hidden" data-testid="route-card">
              <div className="h-[280px]">
                <TransportMap
                  departure={departureAddress}
                  arrival={arrivalAddress}
                  stops={stops.slice(1, -1).map(s => s.address).filter(Boolean)}
                  showDistanceBadge={true}
                />
              </div>
              {/* Green gradient bar */}
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-green-300" />
                    <div>
                      <p className="text-emerald-100 text-xs">Départ</p>
                      <p className="font-semibold">{departureCity}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <Route className="w-5 h-5 mx-auto mb-1 opacity-80" />
                      {transfer.distance && (
                        <p className="text-sm font-medium">{transfer.distance} km</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-emerald-100 text-xs">Arrivée</p>
                      <p className="font-semibold">{arrivalCity}</p>
                    </div>
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                  </div>
                </div>
              </div>
            </Card>

            {/* Timeline progression étapes (lecture seule) — PM Spec 01/06/2026 */}
            {stops.length > 0 && (
              <TransferStopsTimeline transfer={transfer} canManage={false} />
            )}

            {/* Trajet - Détail des arrêts */}
            <Card data-testid="stops-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Route className="w-5 h-5 text-gray-400" />
                  Trajet
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stops.length > 0 ? (
                  <div className="space-y-4">
                    {stops.map((stop, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="flex flex-col items-center">
                          <div className={cn(
                            "w-3 h-3 rounded-full",
                            idx === 0 ? "bg-emerald-500" :
                            idx === stops.length - 1 ? "bg-red-500" :
                            "bg-blue-500"
                          )} />
                          {idx < stops.length - 1 && (
                            <div className="w-0.5 h-8 bg-gray-200 mt-1" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500 uppercase">
                              {idx === 0 ? 'DÉPART' : idx === stops.length - 1 ? 'ARRIVÉE' : `ÉTAPE ${idx}`}
                            </span>
                            {transfer?.status && (() => {
                              const derived = getDerivedStopStatus(transfer.status, idx, stops.length);
                              return (
                                <Badge variant="outline" className={cn("text-xs border", derived.cls)}>
                                  {derived.label}
                                </Badge>
                              );
                            })()}
                          </div>
                          <p className="text-sm font-medium text-gray-900 mt-0.5">
                            <MapPin className="w-3.5 h-3.5 inline mr-1 text-gray-400" />
                            {stop.address || stop.location || 'Adresse non renseignée'}
                          </p>
                          {(stop.arrivedAt || stop.departedAt) && (
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                              {stop.arrivedAt && <span>Arrivée: {formatTime(stop.arrivedAt)}</span>}
                              {stop.departedAt && <span>Départ: {formatTime(stop.departedAt)}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-400">
                    <MapPin className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Aucun arrêt défini</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Demandes de modification — Phase 3a */}
            <TransferModificationRequestsSection
              contractId={contractId}
              runId={runId}
              run={transfer}
              canRequest={!['completed', 'delivered', 'terminee', 'cancelled', 'annulee'].includes(
                (transfer?.status || '').toLowerCase()
              )}
            />

            {/* Détails de l'Opération */}
            <Card data-testid="operation-details-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Détails de l'Opération</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Row 1 */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <Truck className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Véhicule</p>
                      <p className="font-medium text-gray-900">{formatVehicleType(transfer.vehicleType, transfer.vehicleTypeOther) || 'Non défini'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <Weight className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Poids</p>
                      <p className="font-medium text-gray-900">{transfer.weight ? `${transfer.weight} T` : '-'}</p>
                    </div>
                  </div>
                </div>
                {/* Row 2 */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Type d'opération</p>
                      <p className="font-medium text-gray-900">{formatOperationType(transfer.operationType, transfer.operationTypeOther) || 'Transport'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                    <Package className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Marchandise</p>
                      <p className="font-medium text-gray-900">{formatMerchandiseType(transfer.merchandiseType, transfer.merchandiseTypeOther) || '-'}</p>
                    </div>
                  </div>
                </div>
                {/* Highlighted cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Truck className="w-4 h-4 text-blue-600" />
                      <p className="text-xs text-blue-600 font-medium">Quantité de véhicules</p>
                    </div>
                    <p className="text-2xl font-bold text-blue-700">{transfer.numberOfTrucks || 1} camion{(transfer.numberOfTrucks || 1) > 1 ? 's' : ''}</p>
                  </div>
                  <div className="p-4 bg-purple-50 border border-purple-100 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Route className="w-4 h-4 text-purple-600" />
                      <p className="text-xs text-purple-600 font-medium">Classification</p>
                    </div>
                    <p className="text-lg font-bold text-purple-700">
                      {transfer.isInternational ? 'INTERLAND' : 'INTRALAND'}
                    </p>
                    <p className="text-xs text-purple-600">
                      {transfer.isInternational ? 'Transport international (CEDEAO)' : 'Transport national'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Opération associée */}
            {transfer.operationId && (
              <Card data-testid="linked-operation-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Package className="w-5 h-5 text-gray-400" />
                    Opération associée
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">
                        {transfer.operationReference || transfer.operationId}
                      </p>
                      {transfer.operationStatus && (
                        <Badge variant="outline" className="text-xs mt-1">
                          {getStatusLabel(transfer.operationStatus)}
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/operations/${transfer.operationId}`)}
                      data-testid="view-operation-btn"
                    >
                      Voir l'opération
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - 35% */}
          <div className="w-[35%] space-y-4">
            {/* Véhicule & Chauffeur */}
            <Card data-testid="vehicle-driver-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Truck className="w-5 h-5 text-gray-400" />
                  Véhicule & Chauffeur
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Véhicule */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">VÉHICULE ASSIGNÉ</p>
                  {transfer.plannedVehiclePlate ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-gray-600" />
                        <span className="font-mono font-bold text-gray-900 text-base" data-testid="vehicle-plate">
                          {transfer.plannedVehiclePlate}
                        </span>
                      </div>
                      {transfer.vehicleDetails && (
                        <div className="space-y-1.5 ml-6">
                          {(transfer.vehicleDetails.brand || transfer.vehicleDetails.model) && (
                            <p className="text-sm text-gray-700 font-medium">
                              {[transfer.vehicleDetails.brand, transfer.vehicleDetails.model].filter(Boolean).join(' ')}
                              {transfer.vehicleDetails.color && <span className="text-gray-400 ml-1">· {transfer.vehicleDetails.color}</span>}
                            </p>
                          )}
                          {transfer.vehicleDetails.genre && (
                            <Badge variant="outline" className="text-xs border-blue-200 bg-blue-50 text-blue-700" data-testid="vehicle-genre">
                              {transfer.vehicleDetails.genre}
                            </Badge>
                          )}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                            {transfer.vehicleDetails.cuKg && (
                              <span data-testid="vehicle-cu">CU: <strong className="text-gray-700">{transfer.vehicleDetails.cuKg} Kg</strong></span>
                            )}
                            {transfer.vehicleDetails.ptacKg && (
                              <span data-testid="vehicle-ptac">PTAC: <strong className="text-gray-700">{transfer.vehicleDetails.ptacKg} Kg</strong></span>
                            )}
                            {transfer.vehicleDetails.energy && (
                              <span>{transfer.vehicleDetails.energy}</span>
                            )}
                          </div>
                        </div>
                      )}
                      {transfer.vehicleType && !transfer.vehicleDetails?.genre && (
                        <Badge variant="outline" className="text-xs mt-1.5 ml-6">{formatVehicleType(transfer.vehicleType)}</Badge>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">Non assigné</p>
                  )}
                </div>

                {/* Chauffeur */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">CHAUFFEUR</p>
                  {transfer.plannedDriverName ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-600" />
                        <span className="font-medium text-gray-900" data-testid="driver-name">
                          {transfer.plannedDriverName}
                        </span>
                      </div>
                      {transfer.plannedDriverPhone && (
                        <div className="flex items-center gap-2 ml-6 text-sm text-gray-600">
                          <Phone className="w-3.5 h-3.5" />
                          <a href={`tel:${transfer.plannedDriverPhone}`} className="hover:text-emerald-600 transition-colors">
                            {transfer.plannedDriverPhone}
                          </a>
                        </div>
                      )}
                      {transfer.driverDetails?.permitCategories && (
                        <div className="ml-6 mt-1" data-testid="driver-categories">
                          <p className="text-[10px] text-gray-400 mb-1">Catégories permis</p>
                          <div className="flex items-center gap-1 flex-wrap">
                            {transfer.driverDetails.permitCategories.map((cat, i) => (
                              <span key={i} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                cat.status === 'expired' ? 'bg-red-100 text-red-600 line-through' :
                                cat.status === 'permanent' ? 'bg-emerald-100 text-emerald-700' :
                                ['C', 'D', 'E', 'CE', 'EC'].includes(cat.category) ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-600'
                              }`} data-testid={`driver-cat-${cat.category}`}>
                                {cat.category}
                                {cat.status === 'expired' && ' ✗'}
                                {cat.status === 'permanent' && ' ∞'}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {transfer.driverDetails && !transfer.driverDetails.permitCategories && transfer.driverDetails.permitType && (
                        <Badge variant="outline" className="text-xs ml-6 mt-1">{transfer.driverDetails.permitType}</Badge>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">Non assigné</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Détails */}
            <Card data-testid="details-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Package className="w-5 h-5 text-gray-400" />
                  Détails
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-0">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Poids</span>
                  <span className="text-sm font-medium flex items-center gap-1">
                    <Scale className="w-3.5 h-3.5 text-gray-400" />
                    {transfer.weight ? `${transfer.weight} T` : '-'}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Nombre de voyages</span>
                  <span className="text-sm font-medium">{transfer.numberOfTrips || 1}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Shift</span>
                  <span className="text-sm font-medium">
                    {transfer.shift === 'day' ? 'Jour' : transfer.shift === 'night' ? 'Nuit' : (transfer.shift || '-')}
                  </span>
                </div>
                {transfer.vehicleType && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Type véhicule</span>
                    <span className="text-sm font-medium">{formatVehicleType(transfer.vehicleType)}</span>
                  </div>
                )}
                {transfer.netWeight > 0 && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Poids net (pesée)</span>
                    <span className="text-sm font-semibold text-emerald-700">{transfer.netWeight?.toFixed(1)} T</span>
                  </div>
                )}
                {permissions.canViewPrices && transfer.price > 0 && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-sm text-gray-500">Prix</span>
                    <span className="text-sm font-semibold">{new Intl.NumberFormat('fr-FR').format(transfer.price)} FCFA</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-3">
                  <span className="text-sm text-gray-500">Créé le</span>
                  <span className="text-sm font-medium">{formatDate(transfer.createdAt)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Status info for completed / cancelled */}
            {transfer.status === 'completed' && (
              <Card>
                <CardContent className="p-5">
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
                    <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                    <p className="font-medium text-emerald-800">Opération terminée</p>
                  </div>
                </CardContent>
              </Card>
            )}
            {transfer.status === 'cancelled' && (
              <Card>
                <CardContent className="p-5">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-center">
                    <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <p className="font-medium text-red-800">Opération annulée</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {transfer.notes && (
              <Card data-testid="notes-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{transfer.notes}</p>
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            {transfer.statusHistory?.length > 0 && (
              <Card data-testid="timeline-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    Historique
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {transfer.statusHistory.map((entry, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="flex flex-col items-center">
                          <div className={cn(
                            "w-2.5 h-2.5 rounded-full",
                            idx === transfer.statusHistory.length - 1 ? "bg-emerald-500 ring-2 ring-emerald-200" : "bg-emerald-400"
                          )} />
                          {idx < transfer.statusHistory.length - 1 && (
                            <div className="w-0.5 h-6 bg-emerald-200 mt-1" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {getStatusLabel(entry.status || entry.to)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(entry.date || entry.changedAt)} {formatTime(entry.date || entry.changedAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientTransferDetail;
